import { useState, useCallback, useRef, useEffect } from 'react'
import { streamAgentSession, streamAgentAnswer, streamAgentFiles, streamToolChat } from '../../../services/api'
import { fetchShapeSuggestions, type ShapeSuggestion } from '../../../services/stockShapeSuggestions'
import type { TerminalEntry, SessionPhase } from './types'
import type { JobWizardConfig, MachineProfile, Tool } from '../../../types'
import type { AgentSessionCallbacks, ToolChatJobContext } from '../../../services/types'
import { makeRectangularStock, STOCK_SHAPES, type ShapeId } from '../../../lib/stock-shapes'

export function useAgentTerminal(
  profiles: MachineProfile[],
  inventoryTools: Tool[],
  onGeometryChange?: (geo: Record<string, unknown> | null) => void,
) {
  const [entries, setEntries] = useState<TerminalEntry[]>([])
  const [phase, setPhase] = useState<SessionPhase>('idle')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isSuggestingTools, setIsSuggestingTools] = useState(false)
  const [shapeSuggestions, setShapeSuggestions] = useState<ShapeSuggestion[] | undefined>(undefined)
  // partGeometry: the most recent extracted part geometry from the backend. Surfaced
  // so the irregular-drawing alignment flow can align the part inside uploaded stock.
  const [partGeometry, setPartGeometry] = useState<Record<string, unknown> | null>(null)
  const configRef = useRef<Partial<JobWizardConfig>>({})
  const suggestedToolsRef = useRef(false)
  const suggestedShapesRef = useRef(false)
  const geometryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingGeometryRef = useRef<Record<string, unknown> | null>(null)

  // Cleanup debounce timer on unmount
  useEffect(() => () => {
    if (geometryTimerRef.current) clearTimeout(geometryTimerRef.current)
  }, [])

  const addEntry = useCallback((entry: TerminalEntry) => {
    setEntries(prev => [...prev, entry])
  }, [])

  // Update an existing status entry by id (running → done/error)
  const updateStatus = useCallback((id: string, status: 'running' | 'done' | 'error', detail?: string) => {
    setEntries(prev => prev.map(e =>
      e.type === 'agent_status' && e.id === id
        ? { ...e, status, detail }
        : e,
    ))
  }, [])

  // Suggest tools and add an agent_tools entry for user review
  const suggestTools = useCallback((config: Partial<JobWizardConfig>, userPrompt?: string) => {
    if (suggestedToolsRef.current) return
    suggestedToolsRef.current = true
    setIsSuggestingTools(true)

    const statusId = crypto.randomUUID()
    setEntries(prev => [...prev, {
      type: 'agent_status' as const,
      id: statusId,
      label: 'Suggesting cutting tools',
      status: 'running' as const,
    }])

    // Build job context for tool chat
    const machineId = config.machineId as string | undefined
    const profile = profiles.find(p => p.id === machineId)
    const jobContext: ToolChatJobContext = {
      operationType: (config.operationType || 'milling') as 'milling' | 'turning',
      material: config.material || 'AL 6061',
      stockShape: config.stockShape || 'Rectangular',
      stockSize: config.stockSize || null,
      machineName: profile?.name || '',
      machineMaxRpm: profile?.maxRpm || 0,
      machineMaxFeedRate: profile?.maxFeedRate || 0,
      machineController: profile?.controller || '',
      notes: config.notes || '',
    }

    const currentTools = inventoryTools.length > 0 ? inventoryTools : []
    const msgContent = userPrompt
      ? `Re-suggest tools for this job. Feedback: ${userPrompt}`
      : 'Suggest an optimized tool kit for this job'
    const messages = [{ role: 'user' as const, content: msgContent }]

    streamToolChat(messages, currentTools, jobContext, {
      onToken: () => {},
      onTools: (newTools) => {
        configRef.current = { ...configRef.current, tools: newTools }
        // Update summary entry too
        setEntries(prev => prev.map(e =>
          e.type === 'agent_summary'
            ? { ...e, config: { ...e.config, tools: newTools } }
            : e,
        ))
      },
      onDone: () => {
        setIsSuggestingTools(false)
        // Update status line
        setEntries(prev => prev.map(e =>
          e.type === 'agent_status' && e.id === statusId
            ? { ...e, status: 'done' as const, detail: `${configRef.current.tools?.length || 0} tools suggested` }
            : e,
        ))
        // Add agent_tools entry for user review
        const tools = configRef.current.tools || []
        setEntries(prev => [...prev, {
          type: 'agent_tools' as const,
          id: crypto.randomUUID(),
          tools,
          confirmed: false,
        }])
        setPhase('tools')
      },
      onError: (message) => {
        setIsSuggestingTools(false)
        setEntries(prev => prev.map(e =>
          e.type === 'agent_status' && e.id === statusId
            ? { ...e, status: 'error' as const, detail: message }
            : e,
        ))
      },
    }).catch((err) => {
      setIsSuggestingTools(false)
      const msg = err instanceof Error ? err.message : String(err)
      setEntries(prev => prev.map(e =>
        e.type === 'agent_status' && e.id === statusId
          ? { ...e, status: 'error' as const, detail: msg }
          : e,
      ))
    })
  }, [profiles, inventoryTools])

  // ─── SSE callbacks ───────────────────────────────────────────────────

  const buildCallbacks = useCallback((onDoneCb?: (sid: string) => void): AgentSessionCallbacks => ({
    onText(text) {
      console.log('[agent]', text)
    },
    onStatus(id, label, status, detail) {
      if (status === 'running') {
        addEntry({ type: 'agent_status', id, label, status, detail })
      } else {
        updateStatus(id, status, detail)
      }
    },
    onPreview(geometry) {
      addEntry({ type: 'agent_preview', id: crypto.randomUUID(), geometry })
      // Expose the latest part geometry to consumers (e.g. the irregular-drawing
      // alignment flow inside StockDimensionInputs needs it to align part-in-stock).
      setPartGeometry(geometry)
      // Debounce geometry updates to avoid flooding Three.js Canvas with re-renders
      pendingGeometryRef.current = geometry
      if (geometryTimerRef.current) clearTimeout(geometryTimerRef.current)
      geometryTimerRef.current = setTimeout(() => {
        onGeometryChange?.(pendingGeometryRef.current)
        pendingGeometryRef.current = null
      }, 300)

      // Fire LLM stock-shape suggester once per session, as soon as geometry arrives.
      // pdfBase64 only reaches the client via the later `agent_summary` event, so
      // most first-fires will be geometry-only — that's expected per spec.
      if (!suggestedShapesRef.current) {
        suggestedShapesRef.current = true
        const pdfB64 = configRef.current.pdfBase64 ?? null
        const drawingImage = pdfB64
          ? { mimeType: 'application/pdf', base64: pdfB64 }
          : undefined
        fetchShapeSuggestions(geometry, drawingImage)
          .then(({ suggestions }) => setShapeSuggestions(suggestions))
          .catch((err: unknown) => {
            // No silent fallback — surface in the terminal per CLAUDE.md.
            const msg = err instanceof Error ? err.message : String(err)
            addEntry({
              type: 'agent_error',
              id: crypto.randomUUID(),
              message: `Shape suggestion failed: ${msg}`,
            })
          })
      }
    },
    onQuestion(field, prompt, inputType, priority, options) {
      // Skip if this field was already asked (avoid duplicate questions)
      setEntries(prev => {
        const alreadyAsked = prev.some(e => e.type === 'agent_question' && e.field === field && !e.answered)
        if (alreadyAsked) return prev
        return [...prev, {
          type: 'agent_question' as const,
          id: crypto.randomUUID(),
          field, prompt, inputType, priority, options,
          answered: false,
        }]
      })
      setPhase('questioning')
    },
    onSummary(config, readyToGenerate, stepValidation) {
      configRef.current = config as Partial<JobWizardConfig>
      const newSummary: TerminalEntry = {
        type: 'agent_summary',
        id: crypto.randomUUID(),
        config: config as Partial<JobWizardConfig>,
        readyToGenerate,
      }
      // Replace existing summary (if any) instead of appending a duplicate
      setEntries(prev => {
        const hasSummary = prev.some(e => e.type === 'agent_summary')
        if (hasSummary) {
          return prev.map(e => e.type === 'agent_summary' ? newSummary : e)
        }
        return [...prev, newSummary]
      })
      if (readyToGenerate) {
        // Show validation bubble (informational) then auto-proceed to tools
        const newValidation: TerminalEntry = {
          type: 'agent_part_validation',
          id: crypto.randomUUID(),
          confirmed: true, // auto-confirmed — not blocking
          stepValidation,
        }
        setEntries(prev => {
          const hasValidation = prev.some(e => e.type === 'agent_part_validation')
          if (hasValidation) {
            return prev.map(e => e.type === 'agent_part_validation' ? newValidation : e)
          }
          return [...prev, newValidation]
        })
        setPhase('tools')
        suggestTools(config as Partial<JobWizardConfig>)
      }
    },
    onError(message) {
      addEntry({ type: 'agent_error', id: crypto.randomUUID(), message })
    },
    onDone(sid) {
      onDoneCb?.(sid)
      setPhase(prev => prev === 'processing' ? 'questioning' : prev)
    },
  }), [addEntry, updateStatus, onGeometryChange, suggestTools])

  // ─── Part validation callbacks ───────────────────────────────────────

  const confirmPart = useCallback(() => {
    setEntries(prev => prev.map(e =>
      e.type === 'agent_part_validation' ? { ...e, confirmed: true } : e,
    ))
    setPhase('tools')
    suggestTools(configRef.current)
  }, [suggestTools])

  const rejectPart = useCallback(async (feedback: string) => {
    if (!sessionId) return
    setEntries(prev => prev.map(e =>
      e.type === 'agent_part_validation' ? { ...e, confirmed: true } : e,
    ))
    addEntry({
      type: 'user_answer',
      id: crypto.randomUUID(),
      field: 'partFeedback',
      displayText: feedback,
    })
    setPhase('processing')
    try {
      await streamAgentAnswer(sessionId, { partFeedback: feedback }, buildCallbacks())
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      addEntry({ type: 'agent_error', id: crypto.randomUUID(), message: msg })
      setPhase('questioning')
    }
  }, [sessionId, addEntry, buildCallbacks])

  // ─── Tool confirmation callbacks ─────────────────────────────────────

  const confirmTools = useCallback(() => {
    setEntries(prev => {
      const toolEntry = prev.find(e => e.type === 'agent_tools' && !e.confirmed)
      if (toolEntry && toolEntry.type === 'agent_tools') {
        configRef.current = { ...configRef.current, tools: toolEntry.tools }
      }
      return prev.map(e =>
        e.type === 'agent_tools' && !e.confirmed ? { ...e, confirmed: true } : e,
      )
    })
    setPhase('ready')
  }, [])

  const updateToolsEntry = useCallback((tools: Tool[]) => {
    setEntries(prev => prev.map(e =>
      e.type === 'agent_tools' && !e.confirmed ? { ...e, tools } : e,
    ))
  }, [])

  const resuggestTools = useCallback((prompt?: string) => {
    setEntries(prev => prev.filter(e => !(e.type === 'agent_tools' && !e.confirmed)))
    suggestedToolsRef.current = false
    suggestTools(configRef.current, prompt)
  }, [suggestTools])

  // ─── File + answer submission ────────────────────────────────────────

  const submitFiles = useCallback(async (
    files: File[],
    machineProfiles: Array<{ id: string; name: string; type: string }>,
  ) => {
    setPhase('processing')
    addEntry({
      type: 'user_files',
      id: crypto.randomUUID(),
      fileNames: files.map(f => f.name),
    })

    try {
      await streamAgentSession(files, machineProfiles, buildCallbacks((sid) => setSessionId(sid)))
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      addEntry({ type: 'agent_error', id: crypto.randomUUID(), message: msg })
      setPhase('questioning')
    }
  }, [addEntry, buildCallbacks])

  const submitAnswer = useCallback(async (field: string, value: string | number) => {
    if (!sessionId) return

    // Mark the question as answered
    setEntries(prev => prev.map(e =>
      e.type === 'agent_question' && e.field === field
        ? { ...e, answered: true }
        : e,
    ))

    // For confirm answers ("yes"), extract the actual value from the prompt
    // so the backend receives the real data, not just "yes"
    let resolvedValue: string | number = value
    let displayText: string

    if (field === 'machineProfile' && typeof value === 'string') {
      const profile = profiles.find(p => p.id === value)
      displayText = profile?.name || String(value)
    } else if (field === 'stockShape' && typeof value === 'string') {
      // Update local config immediately so the next question's preview
      // (e.g. stock_dimensions_preview) reads the new shape, not the stale default.
      // Also reset stockSize since its dimension keys are tied to the previous shape.
      configRef.current = {
        ...configRef.current,
        stockShape: value,
        stockSize: null,
      }
      const def = STOCK_SHAPES[value as ShapeId]
      displayText = def?.label ?? String(value)
    } else if (
      (field === 'stockDims' || field === 'stockSize') &&
      typeof value === 'string' &&
      value.trim().startsWith('{')
    ) {
      // Parse shape-aware stock payload: { shapeId, dimensions, x, y, z }
      // Mirrors the workOrigin JSON-blob pattern.
      try {
        const parsed = JSON.parse(value) as {
          shapeId?: ShapeId
          dimensions?: Record<string, number>
          x?: number
          y?: number
          z?: number
        }
        if (parsed.shapeId && parsed.dimensions) {
          const def = STOCK_SHAPES[parsed.shapeId]
          // Update local config so summary renders shape-aware
          configRef.current = {
            ...configRef.current,
            // Always use the shape from the dims payload — it's the source of truth
            // for what shape the user actually saw when entering dimensions.
            stockShape: parsed.shapeId,
            stockSize: {
              shapeId: parsed.shapeId,
              dimensions: parsed.dimensions,
              x: parsed.x ?? 0,
              y: parsed.y ?? 0,
              z: parsed.z ?? 0,
            },
          }
          displayText = def.formatSummary(parsed.dimensions, 'mm')
        } else {
          displayText = String(value)
        }
      } catch {
        displayText = String(value)
      }
    } else if (field === 'workOrigin' && typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) {
          displayText = parsed.map((o: any) => `${o.register} X${o.x} Y${o.y} Z${o.z}`).join('  \u00B7  ')
        } else {
          displayText = String(value)
        }
      } catch {
        const [x, y, z] = value.split(',')
        displayText = `G54 X${x} Y${y} Z${z}`
      }
    } else if (String(value).toLowerCase() === 'yes') {
      // User confirmed a suggestion — extract the actual value from the prompt
      const question = entries.find(e => e.type === 'agent_question' && e.field === field)
      const prompt = (question && question.type === 'agent_question') ? question.prompt : ''

      // Strip markdown formatting before matching
      const clean = prompt.replace(/\*\*/g, '').replace(/["""\u201C\u201D]/g, '"')

      // Extract value: try number first (for depth/thickness fields),
      // then text between "as X." or "material is X." patterns
      const numMatch = clean.match(/(\d+(?:\.\d+)?)\s*(?:mm|in)/)
      // "material as X." or "material is X." or "Material: X." — capture X
      const materialMatch = clean.match(/material\s+(?:as|is|:)\s+(.+?)(?:\.\s*[Cc]onfirm|,|\?|$)/i)
        ?? clean.match(/material[:\s]+([A-Z][A-Za-z0-9\s()\-\/]+?)(?:\.\s*[Cc]onfirm|,|\?|$)/i)

      if (numMatch && /depth|thick|height|stock.*z/i.test(clean)) {
        resolvedValue = parseFloat(numMatch[1])
        displayText = `${resolvedValue} mm (confirmed)`
      } else if (materialMatch) {
        resolvedValue = materialMatch[1].trim()
        displayText = `${resolvedValue} (confirmed)`
      } else if (numMatch) {
        resolvedValue = parseFloat(numMatch[1])
        displayText = `${resolvedValue} mm (confirmed)`
      } else {
        // Fallback: grab everything between "of/as/is" and the next period
        const fallback = clean.match(/(?:of|as|is)\s+(.+?)(?:\.\s|$)/i)
        if (fallback) {
          resolvedValue = fallback[1].trim()
          displayText = `${resolvedValue} (confirmed)`
        } else {
          displayText = 'Confirmed'
        }
      }
    } else if (typeof value === 'number') {
      displayText = `${value} mm`
    } else {
      displayText = String(value)
    }
    addEntry({
      type: 'user_answer',
      id: crypto.randomUUID(),
      field,
      displayText,
    })

    setPhase('processing')

    try {
      await streamAgentAnswer(sessionId, { [field]: resolvedValue }, buildCallbacks())
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      addEntry({ type: 'agent_error', id: crypto.randomUUID(), message: msg })
      setPhase('questioning')
    }
  }, [sessionId, addEntry, buildCallbacks])

  const submitAdditionalFiles = useCallback(async (field: string, files: File[]) => {
    if (!sessionId || files.length === 0) return

    // Mark the file_upload question as answered
    setEntries(prev => prev.map(e =>
      e.type === 'agent_question' && e.field === field
        ? { ...e, answered: true }
        : e,
    ))

    addEntry({
      type: 'user_files',
      id: crypto.randomUUID(),
      fileNames: files.map(f => f.name),
    })

    setPhase('processing')

    try {
      await streamAgentFiles(sessionId, files, buildCallbacks())
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      addEntry({ type: 'agent_error', id: crypto.randomUUID(), message: msg })
      setPhase('questioning')
    }
  }, [sessionId, addEntry, buildCallbacks])

  // Edit a field from the summary — re-submit to backend
  const editSummaryField = useCallback(async (field: string, value: string | number) => {
    if (!sessionId) return

    // Update the summary entry config locally
    setEntries(prev => prev.map(e => {
      if (e.type !== 'agent_summary') return e
      const updated = { ...e.config }
      if (field === 'material') updated.material = String(value)
      if (field === 'stockShape' && typeof value === 'string') {
        // Shape changed — existing dimensions are structurally invalid (e.g. X/Y/Z
        // for a round_bar that needs Ø/L). Clear them; re-asking is handled below.
        updated.stockShape = value
        updated.stockSize = null
      }
      if (field === 'stockDims' && typeof value === 'string') {
        const parts = value.split(/[,×x]/).map(s => parseFloat(s.trim()))
        if (parts.length >= 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
          updated.stockSize = makeRectangularStock(parts[0], parts[1], parts[2])
        }
      }
      configRef.current = updated
      return { ...e, config: updated }
    }))

    // Re-submit to backend so the orchestrator knows
    try {
      await streamAgentAnswer(sessionId, { [field]: value }, buildCallbacks())
    } catch {
      // Silent — the local update already took effect
    }

    // When shape changes, dimensions are stale — re-open the dims question so
    // the user re-enters them with the new shape's input fields.
    if (field === 'stockShape') {
      setEntries(prev => prev
        .filter(e => !(e.type === 'user_answer' && e.field === 'stockDims'))
        .map(e =>
          e.type === 'agent_question' && e.field === 'stockDims'
            ? { ...e, answered: false }
            : e,
        ),
      )
      setPhase('questioning')
    }
  }, [sessionId, buildCallbacks])

  // Re-open any answered question for editing
  // Also strip summary, validation, and tools entries — the backend will
  // re-send them after the user re-answers, preventing duplicates.
  const editAnswer = useCallback((field: string) => {
    setEntries(prev => prev
      .filter(e =>
        !(e.type === 'user_answer' && e.field === field) &&
        e.type !== 'agent_summary' &&
        e.type !== 'agent_part_validation' &&
        e.type !== 'agent_tools',
      )
      .map(e =>
        e.type === 'agent_question' && e.field === field
          ? { ...e, answered: false }
          : e,
      ),
    )
    suggestedToolsRef.current = false
    setPhase('questioning')
  }, [])

  // Re-open machine picker — unmark the question as answered
  const editMachine = useCallback(() => editAnswer('machineProfile'), [editAnswer])

  // Re-open tools editor — unconfirm the tools entry
  const editTools = useCallback(() => {
    setEntries(prev => prev.map(e =>
      e.type === 'agent_tools' && e.confirmed
        ? { ...e, confirmed: false }
        : e,
    ))
    setPhase('tools')
  }, [])

  return {
    entries,
    phase,
    sessionId,
    config: configRef.current,
    isSuggestingTools,
    shapeSuggestions,
    partGeometry,
    submitFiles,
    submitAnswer,
    submitAdditionalFiles,
    confirmPart,
    rejectPart,
    confirmTools,
    updateToolsEntry,
    resuggestTools,
    editSummaryField,
    editAnswer,
    editMachine,
    editTools,
  }
}
